<template>
  <v-card v-if="editMode" class="invoice-edit-items" outlined>
    <div v-if="InvoiceDetails.length" class="invoice-edit-items__list">
      <article
        v-for="(item, index) in InvoiceDetails"
        :key="item.id || `invoice-item-${index}`"
        class="invoice-edit-row"
        :class="{ 'invoice-edit-row--deleted': item.delete }"
      >
        <div class="invoice-edit-row__identity">
          <div class="invoice-edit-row__person">
            <v-icon size="18" color="primary">mdi-account-outline</v-icon>
            <div>
              <strong>{{ item.studentName || item.name }}</strong>
              <span v-if="item.description">{{ item.description }}</span>
              <span v-else-if="item.studentName">Student charge</span>
            </div>
          </div>
          <v-text-field
            v-if="!item.delete"
            v-model="item.name"
            label="Charge name"
            outlined
            hide-details
            dense
            @focus="$event.target.select()"
          />
          <span v-else class="invoice-edit-row__deleted-name">{{ item.name }}</span>
          <div v-if="payerDetails(item).length" class="invoice-edit-row__split">
            <span v-for="detail in payerDetails(item)" :key="detail.label">
              <small>{{ detail.label }}</small>
              <strong>{{ detail.amount | currency }}</strong>
            </span>
            <span v-if="allocationExceedsTotal(item)" class="invoice-edit-row__split-warning">
              <v-icon size="14" color="warning">mdi-alert-outline</v-icon>
              Payment split exceeds total
            </span>
          </div>
        </div>

        <div class="invoice-edit-row__field invoice-edit-row__price">
          <v-text-field
            v-if="!item.delete"
            v-model="item.price"
            label="Amount"
            outlined
            :min="0"
            prefix="$"
            class="amount-input"
            type="number"
            hide-details
            dense
            @blur="checkPrice(item)"
            @focus="$event.target.select()"
          />
          <span v-else>{{ item.price | currency }}</span>
          <v-tooltip v-if="!item.delete" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="invoice-edit-row__copy"
                icon
                small
                v-bind="attrs"
                v-on="on"
                :aria-label="`Apply ${item.name} price to matching items`"
                @click="copyPrice(item, index)"
              >
                <v-icon size="18">mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>Apply this price to matching charges</span>
          </v-tooltip>
        </div>

        <div class="invoice-edit-row__total">
          <small>Line total</small>
          <strong :class="{ 'error--text': Number(item.price) < 0 }">
            {{ (item.qty * item.price) | currency }}
          </strong>
        </div>

        <div class="invoice-edit-row__action">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="!item.delete"
                icon
                color="error"
                v-bind="attrs"
                v-on="on"
                :aria-label="`Remove ${item.studentName || item.name}`"
                @click="deleteItem(index)"
              >
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
              <v-btn
                v-else
                icon
                color="primary"
                v-bind="attrs"
                v-on="on"
                :aria-label="`Restore ${item.studentName || item.name}`"
                @click="unDeleteItem(index)"
              >
                <v-icon>mdi-restore</v-icon>
              </v-btn>
            </template>
            <span>{{ item.delete ? "Restore charge" : "Remove charge" }}</span>
          </v-tooltip>
        </div>
      </article>
    </div>
    <div v-else class="invoice-edit-items__empty">
      <v-icon size="32" color="grey lighten-1">mdi-receipt-text-outline</v-icon>
      <strong>No invoice items</strong>
      <span>Add a student charge or product above.</span>
    </div>
  </v-card>

  <v-card v-else outlined>
    <v-card-text>
      <!-- <v-layout row wrap align-center>
        <v-flex shrink v-if="InvoiceDetails.length > 0">
          <v-btn-toggle v-model="showCost" color="primary" dense group>
            <v-btn :value="1" text class="ma-0" outlined>
              <v-icon>mdi-currency-usd</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-flex>
        <v-flex shrink>
          <ReturnPhone @submit="insertReturn" />
        </v-flex>
        <v-flex>
          <v-chip
            v-if="totalQty !== 0"
            class="ma-2"
            color="indigo lighten-1"
            text-color="white"
          >
            <v-avatar left color="indigo " class="mr-2">
              <v-icon small color="white">mdi-cart-outline</v-icon>
            </v-avatar>
            <span class="title">{{ totalQty }}</span>
          </v-chip>
          <v-tooltip bottom v-if="replaceQty !== 0">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                class="ma-2"
                color="yellow lighten-2"
                text-color="black"
              >
                <v-avatar left color="yellow" class="mr-2">
                  <v-icon small color="black">mdi-barcode-scan</v-icon>
                </v-avatar>
                <span class="title">{{ replaceQty }}</span>
              </v-chip>
            </template>
            <span>Serial Item need tobe replace</span>
          </v-tooltip>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex xs12 sm4>
          <v-text-field
            v-model="search"
            outlined
            prepend-inner-icon="search"
            rounded
            dense
            label="Search"
            single-line
            hide-details
            clearable
          >
          </v-text-field>
        </v-flex>
      </v-layout> -->

      <v-data-table
        id="invoice-cart"
        :search="search"
        :headers="headers"
        hide-default-footer
        :itemsPerPage="1000"
        no-data-text="No item on this invoice yet"
        :items="InvoiceDetails"
      >
        <template v-slot:item="{ item, index }">
          <tr :style="{ 'background-color': activeColor(item) }">
            <td>
              <v-layout align-center justify-start row fill-height wrap>
                <v-btn icon @click="deleteItem(index)" v-if="!item.delete">
                  <v-icon color="red">delete</v-icon>
                </v-btn>
                <v-btn icon @click="unDeleteItem(index)" v-else>
                  <v-icon color="green">mdi-delete-empty</v-icon>
                </v-btn>
                <div class="invoice-cart__item-details">
                  <v-text-field
                    v-if="!item.delete"
                    v-model="item.name"
                    outlined
                    hide-details
                    @focus="$event.target.select()"
                    dense
                  ></v-text-field>
                  <span v-else :class="{ deleted: item.delete }">{{
                    item.name
                  }}</span>
                  <div v-if="item.studentName || item.description" class="invoice-cart__context" :class="{ deleted: item.delete }">
                    <span v-if="item.studentName">
                      <v-icon x-small>mdi-account-outline</v-icon>{{ item.studentName }}
                    </span>
                    <span v-if="item.description">
                      <v-icon x-small>mdi-school-outline</v-icon>{{ item.description }}
                    </span>
                  </div>
                  <div v-if="payerDetails(item).length" class="invoice-cart__payer-details" :class="{ deleted: item.delete }">
                    <span v-for="detail in payerDetails(item)" :key="detail.label">
                      {{ detail.label }}: {{ detail.amount | currency }}
                    </span>
                  </div>
                </div>
              </v-layout>
            </td>
            
            <td class="text-center">
              <v-text-field
                v-if="!item.delete"
                v-model="item.qty"
                :disabled="item.imeiNumber ? true : false"
                outlined
                type="number"
                class="centered-input"
                hide-details
                dense
                @focus="$event.target.select()"
              ></v-text-field>
              <span v-else :class="{ deleted: item.delete }">{{
                item.qty
              }}</span>
            </td>
            <td class="text-right">
              <v-text-field
                v-if="!item.delete"
                v-model="item.price"
                outlined
                :min="0"
                prepend-inner-icon="mdi-clipboard-check-outline"
                class="al-r"
                type="number"
                @click:prepend-inner="copyPrice(item, index)"
                hide-details
                @blur="checkPrice(item)"
                @focus="$event.target.select()"
                dense
              ></v-text-field>
              <span v-else :class="{ deleted: item.delete }">{{
                item.price | currency
              }}</span>
            </td>
            <td class="text-right">
              <span
                :class="{ deleted: item.delete }"
                :style="{
                  color:
                    parseFloat(item.price) > parseFloat(item.cost)
                      ? 'green'
                      : 'red',
                }"
                >{{ (item.qty * item.price) | currency }}</span
              >
              <span v-if="showCost">
                <span v-if="checkRightStatus(42)">
                  <v-text-field
                    v-if="showCost"
                    v-model="item.cost"
                    outlined
                    class="al-r"
                    hide-details
                    @focus="$event.target.select()"
                    dense
                  ></v-text-field>
                </span>

                <span v-else>/{{ item.cost | currency }}</span>
              </span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
// const ReturnPhone = () => import("./ReturnPhone");

import { mapActions, mapGetters } from "vuex";

export default {
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openDialog: false,
      selectedItem: null,
      htmlHeight: 0,
      showCost: false,
      search: null,
      headers: [
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: false,
        },
        {
          text: "Quantity",
          align: "center",
          value: "qty",
          width: "100",
          sortable: false,
        },
        {
          text: "Price",
          align: "right",
          value: "price",
          sortable: false,
        },
        {
          text: "Total",
          align: "right",
          value: "total",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("invoice", [
      "InvoiceDetails",
      "totalQty",
      "getSelectedCustomer",
    ]),
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    replaceQty() {
      let value = 0;
      this.InvoiceDetails.map((row) => {
        if (row.needIMEINumber && !row.delete) value += row.qty;
      });

      return value;
    },
    selectedCustomer: {
      get: function () {
        return this.getSelectedCustomer;
      },
      set: function (val) {
        this.SetSelectedDonor(val);
      },
    },
  },
  // components: {
  //   ReturnPhone,
  // },
  watch: {
    selectedCustomer() {
      this.changeHeight();
    },
  },
  mounted() {
    if (this.editMode) return;
    this.htmlHeight = document.documentElement.offsetHeight;
    this.changeHeight();
  },
  methods: {
    ...mapActions("invoice", [
      "AddInvoiceDetail",
      "SetSelectedDonor",
      "DeleteInvoiceDetailRow",
      "UnDeleteInvoiceDetailRow",
      "CopyPriceToAll",
    ]),
    openIMEICollect(item) {
      console.log("click here");
      this.openDialog = true;
      this.selectedItem = item;
    },
    activeColor(item) {
      let result = "transparent";
      if (item.receivedAt) result = "#FFFDE7";
      return result;
    },
    payerDetails(item) {
      const hasBreakdown = item.parentPay !== null && item.parentPay !== undefined
        || Number(item.thirdPartyPay || 0) !== 0
        || Number(item.zakat || 0) !== 0
        || Number(item.payrollDeduction || 0) !== 0;
      if (!hasBreakdown) return [];

      const price = Number(item.price || 0);
      const parent = Number(item.parentPay || 0);
      const sponsor = Number(item.thirdPartyPay || 0);
      const zakat = Number(item.zakat || 0);
      const payroll = Number(item.payrollDeduction || 0);
      const masjid = Math.max(0, price - parent - sponsor - zakat - payroll);
      const quantity = Number(item.qty || 1);
      return [
        { label: "Parent", amount: parent * quantity },
        { label: "Masjid", amount: masjid * quantity },
        { label: "Sponsor", amount: sponsor * quantity },
        { label: "Zakat", amount: zakat * quantity },
        { label: "Payroll", amount: payroll * quantity },
      ].filter(detail => detail.amount !== 0 || detail.label === "Parent");
    },
    allocationExceedsTotal(item) {
      const allocated = Number(item.parentPay || 0)
        + Number(item.thirdPartyPay || 0)
        + Number(item.zakat || 0)
        + Number(item.payrollDeduction || 0);
      return allocated > Number(item.price || 0);
    },
    changeHeight() {
      if (this.editMode) return;
      let html = this.htmlHeight;
      let alert = document.getElementById("alert-col");

      if (alert) {
        alert = alert.offsetHeight;
      } else {
        alert = 0;
      }
      let newHeight = html - (580 + alert) + "px";
      const invoiceCart = document.getElementById("invoice-cart");
      if (invoiceCart) invoiceCart.style.height = newHeight;
    },
    copyPrice(item, index) {
      this.$swal({
        title: "Change Price To All",
        text: `You want to change all ${
          item.name
        } price to ${this.$options.filters.currency(item.price)}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.CopyPriceToAll(index);
        }
      });
    },
    async unDeleteItem(i) {
      await this.UnDeleteInvoiceDetailRow(i);
      this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        })
        .fire({
          icon: "success",
          title: "Item was restore",
        });
    },
    async deleteItem(i) {
      await this.DeleteInvoiceDetailRow(i);
      this.$swal
      .mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      })
      .fire({
        icon: "success",
        title: "Item deleted",
      });
    },
    checkPrice(item) {
      if (parseFloat(item.price) < item.cost) {
        this.$swal({
          title: "Selling Blow Cost Price",
          text: `${item.name} cost price is ${this.$options.filters.currency(
            item.cost
          )}. You are selling it for ${this.$options.filters.currency(
            item.price
          )}below cost price. Are you sure?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#B71C1C",
          cancelButtonColor: "#43A047",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then((result) => {
          if (!result.value) {
            item.price = item.previousPrice;
            item.total = parseFloat(item.price) * parseFloat(item.qty);
          }
        });
      } else {
        item.total = parseFloat(item.price) * parseFloat(item.qty);
      }
    },
    insertReturn(val) {
      console.log("insert return", val);
      const imeiResult = this.InvoiceDetails.find(
        ({ imeiNumber }) => imeiNumber === val.imeiNumber
      );

      if (imeiResult) {
        this.$swal({
          title: "Return Item",
          text: "This item is already in list",
          icon: "error",
        });
        // this.$swal
        //   .mixin({
        //     toast: true,
        //     position: "top-right",
        //     showConfirmButton: true,
        //     timer: 3000,
        //   })
        //   .fire({
        //     icon: "error",
        //     title: `${val.imeiNumber} is already in this list`,
        //   });
      } else {
        this.AddInvoiceDetail({
          ProductId: val.ProductId,
          name: val.name,
          price: Math.abs(val.returnPrice),
          qty: -1,
          total: val.price * -1,
          cost: val.cost,
          imeiNumber: val.imeiNumber,
          lock: true,
        });
      }
    },
  },
};
</script>

<style scoped>
#invoice-cart {
  overflow: auto;
}

.invoice-cart__item-details {
  width: 100%;
  min-width: 230px;
  padding: 4px 0;
}

.invoice-cart__context,
.invoice-cart__payer-details {
  display: flex;
  margin-top: 5px;
  color: #68766d;
  font-size: 12px;
  gap: 6px 14px;
  flex-wrap: wrap;
}

.invoice-cart__context span,
.invoice-cart__payer-details span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.invoice-cart__payer-details {
  color: #3f6f49;
  font-weight: 600;
}

.invoice-edit-items {
  overflow: hidden;
  border-color: #dfe7e0;
  border-radius: 12px;
}

.invoice-edit-items__list {
  display: flex;
  flex-direction: column;
}

.invoice-edit-row {
  display: grid;
  padding: 18px 20px;
  align-items: center;
  border-bottom: 1px solid #e8eee9;
  column-gap: 18px;
  grid-template-columns: minmax(300px, 1fr) minmax(185px, 220px) 120px 44px;
}

.invoice-edit-row:last-child {
  border-bottom: 0;
}

.invoice-edit-row:hover {
  background: #fafcfa;
}

.invoice-edit-row--deleted {
  background: #f7f8f7;
  color: #8a948d;
}

.invoice-edit-row__identity {
  min-width: 0;
}

.invoice-edit-row__person {
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;
  gap: 9px;
}

.invoice-edit-row__person > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.invoice-edit-row__person strong {
  overflow: hidden;
  color: #26372c;
  font-size: 15px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-edit-row__person span {
  margin-top: 2px;
  overflow: hidden;
  color: #718077;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-edit-row__split {
  display: flex;
  margin-top: 10px;
  gap: 8px;
  flex-wrap: wrap;
}

.invoice-edit-row__split > span {
  display: inline-flex;
  padding: 5px 8px;
  align-items: baseline;
  border: 1px solid #dce9de;
  border-radius: 6px;
  color: #355e3b;
  background: #f5faf6;
  gap: 5px;
}

.invoice-edit-row__split small {
  color: #6d7e72;
  font-size: 11px;
}

.invoice-edit-row__split strong {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.invoice-edit-row__split .invoice-edit-row__split-warning {
  border-color: #eadcae;
  color: #7c6116;
  background: #fff9e8;
  font-size: 11px;
}

.invoice-edit-row__field {
  min-width: 0;
}

.invoice-edit-row__price {
  display: flex;
  align-items: center;
  gap: 4px;
}

.invoice-edit-row__copy {
  flex: 0 0 auto;
  color: #68766d;
}

.invoice-edit-row__total {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
}

.invoice-edit-row__total small {
  color: #7b887f;
  font-size: 11px;
}

.invoice-edit-row__total strong {
  margin-top: 3px;
  color: #26372c;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.invoice-edit-row__action {
  display: flex;
  justify-content: flex-end;
}

.invoice-edit-row__deleted-name {
  text-decoration: line-through;
}

.invoice-edit-items__empty {
  display: flex;
  min-height: 180px;
  padding: 32px;
  align-items: center;
  justify-content: center;
  color: #718077;
  flex-direction: column;
  gap: 6px;
}

.invoice-edit-items__empty strong {
  color: #334239;
}

@media (max-width: 1100px) {
  .invoice-edit-row {
    grid-template-columns: minmax(260px, 1fr) 180px 105px 44px;
    column-gap: 12px;
  }
}

@media (max-width: 760px) {
  .invoice-edit-row {
    padding: 16px;
    grid-template-columns: minmax(130px, 1fr) auto;
    row-gap: 14px;
  }

  .invoice-edit-row__identity {
    grid-column: 1 / -1;
  }

  .invoice-edit-row__price {
    grid-column: 1;
  }

  .invoice-edit-row__total {
    grid-column: 2;
  }

  .invoice-edit-row__action {
    position: absolute;
    right: 12px;
  }

  .invoice-edit-row {
    position: relative;
  }
}
</style>
